import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Modal, APIErrorMessage, ButtonContainer } from "shared/components";
import { Spacer } from "shared/layout";
import { copy } from "shared/assets";
import { User } from "shared/assets/icons";
import { Button, Loader } from "shared/components";
import { setMessageModal } from "shared/components/MessageModal";
import { IAPIState, IStatus, ITheme } from "shared/types";
import { dataMetaSelector, venueSelectorCreator, requestPublishVenue } from "Venues/state";
import { VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";

export interface RequestPublishModalProps {
  venueId: string;
  handleClose: () => void;
}

export const RequestPublishModal: FC<RequestPublishModalProps> = ({ venueId, handleClose }) => {
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(dataMetaSelector(VENUE_ACTIONS.requestPublishVenue));

  const venueIdSelector = useMemo(() => venueSelectorCreator(venueId), [venueId]);
  const venue = useSelector(venueIdSelector);

  useClearActionMetaOnUnMount(VENUE_ACTIONS.requestPublishVenue);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(
        setMessageModal({
          message: {
            status: IStatus.success,
            body: copy.requestPublishVenue.success,
          },
        })
      );
      handleClose();
    }
  }, [apiMeta, dispatch, handleClose]);

  const handleRequestPublish = () => {
    dispatch(requestPublishVenue(venueId));
  };

  return (
    <Modal
      title={`${copy.requestPublishVenue.cta} ${venue?.title}`}
      icon={User}
      handleClose={handleClose}
      dataTestId="request_publish_modal"
    >
      <Spacer size="l">
        <div>{copy.requestPublishVenue.message}</div>
        <ButtonContainer>
          <Button onClick={handleRequestPublish} data-testid="request_publish_button">
            <span>{copy.requestPublishVenue.cta}</span>
            {apiMeta?.status === IAPIState.pending && <Loader size="s" />}
          </Button>
          <Button theme={ITheme.secondary} onClick={handleClose}>
            {copy.form.cancel}
          </Button>
        </ButtonContainer>
        {!!apiMeta?.error && <APIErrorMessage apiErrorMessage={true} apiError={apiMeta?.error} />}
      </Spacer>
    </Modal>
  );
};
